import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { T, useTranslate } from '@tolgee/react'
import { Field, useFormState } from 'react-final-form'
import { frequencyOptions } from '../_utils/constants'
import { TimePicker } from '@/components/ui/time-picker'
import { DatePicker } from '@/components/ui/date-picker'
import dayjs from 'dayjs'
import { Input } from '@/components/ui/input'
import { AgentRegistry, AgentToolInput } from '@/hooks/api/use-agent-registry-query'
import { cn } from '@/lib/utils'
import { ScheduleFormData } from '../_utils/types'

export function ScheduleFormFieldAgent({
  agentsData,
  disabled,
}: {
  agentsData: AgentRegistry[]
  disabled: boolean
}) {
  const { t } = useTranslate()
  return (
    <Field name='agentId'>
      {({ input }) => (
        <div className={cn('flex flex-col gap-2', { 'my-4': !input.value })}>
          <p className='font-light'>
            <T keyName='schedule.addButton.fields.agent' />
          </p>
          <Select value={input.value} onValueChange={input.onChange} disabled={disabled}>
            <SelectTrigger isDirty className='w-1/2'>
              <SelectValue placeholder={t('schedule.dialogs.placeholders.selectAgent')} />
            </SelectTrigger>
            <SelectContent>
              {agentsData?.map((agent) => (
                <SelectItem key={agent.name} value={agent.id}>
                  {agent.name}
                </SelectItem>
              )) ?? []}
            </SelectContent>
          </Select>
        </div>
      )}
    </Field>
  )
}

export function ScheduleFormFieldStartDate({ disabled }: { disabled: boolean }) {
  const { t } = useTranslate()
  const form = useFormState<ScheduleFormData>()
  return (
    <Field name='startDate'>
      {({ input }) => (
        <div className='flex items-center gap-2'>
          <Label variant={'schedule-field'} htmlFor='start-date' className='w-20 flex-shrink-0'>
            <T keyName='schedule.addButton.fields.startDate' />
          </Label>
          <div className='flex-1'>
            <DatePicker
              title={t('schedule.addButton.fields.startDate')}
              date={input.value}
              disabled={disabled}
              calendarProps={{
                defaultMonth: form.values?.startDate || new Date(),
                mode: 'single',
                required: true,
                selected: input.value,
                onSelect: (date) => input.onChange(date),
                disabled: (() => {
                  const today = new Date()
                  const endDate = form.values?.endDate
                  if (endDate && endDate >= today) {
                    return [{ before: today }, { after: endDate }]
                  }
                  return { before: today }
                })(),
                showOutsideDays: false,
              }}
            />
          </div>
        </div>
      )}
    </Field>
  )
}

export function ScheduleFormFieldEndDate({ disabled }: { disabled: boolean }) {
  const { t } = useTranslate()
  const form = useFormState<ScheduleFormData>()
  return (
    <Field name='endDate'>
      {({ input }) => (
        <div className='flex items-center gap-2'>
          <Label variant={'schedule-field'} htmlFor='finish-date' className='w-20 flex-shrink-0'>
            <T keyName='schedule.addButton.fields.endDate' />
          </Label>
          <DatePicker
            title={t('schedule.addButton.fields.endDate')}
            date={input.value}
            disabled={disabled}
            calendarProps={{
              defaultMonth: form.values?.endDate,
              mode: 'single',
              required: false,
              selected: input.value,
              onSelect: (date) => {
                input.onChange(date ? dayjs(date).endOf('day') : undefined)
              },
              disabled: (() => {
                const today = new Date()
                const startDate = form.values?.startDate

                // Determine the minimum selectable date: max(today, startDate)
                const minDate = startDate && startDate > today ? startDate : today

                return [{ before: minDate }]
              })(),
              showOutsideDays: false,
            }}
          />
        </div>
      )}
    </Field>
  )
}

export function ScheduleFormFieldStartTime({ disabled }: { disabled: boolean }) {
  const { t } = useTranslate()
  return (
    <Field name='startTime'>
      {({ input }) => (
        <div className='flex items-center gap-2'>
          <Label variant={'schedule-field'} htmlFor='time' className='w-20 flex-shrink-0'>
            <T keyName='schedule.addButton.fields.time' />
          </Label>
          <div className='flex-1'>
            <TimePicker
              time={input.value}
              onChange={input.onChange}
              title={t('schedule.addButton.fields.time')}
              disabled={disabled}
              interval={30}
              use12Hour={true}
            />
          </div>
        </div>
      )}
    </Field>
  )
}

export function ScheduleFormFieldFrequency({ disabled }: { disabled: boolean }) {
  const { t } = useTranslate()
  return (
    <Field name='frequency'>
      {({ input }) => (
        <div className='flex items-center gap-2'>
          <Label variant={'schedule-field'} htmlFor='frequency' className='w-20 flex-shrink-0'>
            <T keyName='schedule.addButton.fields.frequency' />
          </Label>
          <div className='flex-1'>
            <Select value={input.value} onValueChange={input.onChange} disabled={disabled}>
              <SelectTrigger compact>
                <SelectValue placeholder={t('schedule.dialogs.placeholders.selectFrequency')} />
              </SelectTrigger>
              <SelectContent>
                {frequencyOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </Field>
  )
}

export function ScheduleFormFieldParameters({
  disabled,
  selectedAgentParameters,
}: {
  disabled?: boolean
  selectedAgentParameters?: AgentToolInput[]
}) {
  const { t } = useTranslate()
  return (
    <div className='flex flex-col gap-2'>
      <p className='font-light'>
        <T keyName='schedule.addButton.fields.agentParameters' />
      </p>
      <div className='max-h-[96px] overflow-y-scroll pr-4'>
        {selectedAgentParameters?.map(({ title }) => {
          return (
            <div key={title} className='flex items-center justify-between gap-2 py-0.5'>
              <p className='text-sm'>{title}</p>
              <Field
                name={`parameters.${title}`}
                defaultValue={''}
                validate={(value) => !value || (value && !value.trim())}
              >
                {({ input }) => (
                  <Input
                    disabled={disabled}
                    placeholder={t('schedule.addButton.fields.agentParameterPlaceholder')}
                    className='h-[28px] w-[260px] text-sm placeholder:text-sm'
                    {...input}
                  />
                )}
              </Field>
            </div>
          )
        })}
      </div>
    </div>
  )
}
